import { createSelector } from "@reduxjs/toolkit";
import { GuardedType, isArray, IsInterface, isRecord, isString, isUnknown } from "generic-type-guard";

import { isUaaOidcUser, UaaClient, UaaOidcUser, UaaSelectors } from "@com.mgmtp.a12.uaa/uaa-authentication-client";

import { Manifest, RuntimeModuleRoleScopeSpec, RuntimeModuleRoleSpec } from "../app/slices/manifest/types";

import { assertNonNullable } from "./assert";
import { isMap } from "./typeGuardUtils";

export const isUser = new IsInterface()
	.with(function (value: unknown): value is UaaOidcUser {
		return isMap(isUnknown)(value) && isUaaOidcUser(value);
	})
	.withProperties({
		profile: new IsInterface()
			.withProperties({ preferred_username: isString })
			.withOptionalProperties({
				realm_access: isRecord("roles", isArray(isString)),
				resource_access: isMap(isRecord("roles", isArray(isString)))
			})
			.get(),
		_roles: isArray(
			new IsInterface()
				.withProperties({
					name: isString,
					accessRights: isArray(
						new IsInterface()
							.withProperties({
								name: isString
							})
							.get()
					)
				})
				.get()
		)
	})
	.get();
export type User = GuardedType<typeof isUser>;

export interface StructuredRole {
	readonly key: string;
	readonly client?: string;
	readonly roles: string[];
}

const selectRoles = createSelector(
	[(user: User) => user.profile.resource_access, (user: User) => user.profile.realm_access],
	(resource_access, realm_access) => {
		const { client_id } = UaaClient.getOidcConfiguration();
		assertNonNullable(client_id);
		return (
			resource_access?.[client_id]?.roles ??
			resource_access?.["modulf_client"]?.roles ??
			realm_access?.roles ??
			[]
		);
	}
);

export function selectPermissionsForFv(fvKey?: string) {
	return (state: Record<string, unknown>) => {
		const user = UaaSelectors.user(state);
		if (!isUser(user) || !fvKey) {
			return [];
		}
		return createSelector([(user: User) => user._roles], roles => {
			if (selectStructuredRoles(user).every(it => it.key !== fvKey)) {
				return [];
			}
			return roles?.find(it => it.name === fvKey)?.accessRights.map(it => it.name) ?? [];
		})(user);
	};
}

export const isAdmin = createSelector([selectRoles], roles => {
	return roles.some(it => it.toLowerCase() === "admin");
});

const ROLES = ["sachbearbeiter_erweitert", "sachbearbeiter", "leiter", "betrachter"] as const;

export function selectIsAdmin(state: Record<string, unknown>) {
	const user = UaaSelectors.user(state);
	if (!isUser(user)) {
		return false;
	}
	return isAdmin(user);
}

export const selectStructuredRoles = createSelector([selectRoles], roles =>
	Object.values(
		roles.reduce<Record<string, (Omit<StructuredRole, "roles"> & { readonly role: string[number] })[]>>(
			(acc, roleString) => {
				const standardRole = ROLES.find(it => roleString.toLowerCase().endsWith("_" + it));
				const role = !standardRole ? roleString.split("_").pop()?.toLowerCase() : standardRole;
				if (!role) {
					return acc;
				}

				const [prefix, key, client] = roleString
					.substring(0, roleString.length - role?.length - 1)
					.split("_")
					.map(it => it.toUpperCase());

				if (prefix !== "MDF") {
					return acc;
				}

				acc[`${key}:${client}`] ??= [];
				acc[`${key}:${client}`].push({ key, client, role });

				return acc;
			},
			{}
		)
	).flatMap(roles => {
		if (roles[0]) {
			return [{ key: roles[0].key, client: roles[0].client, roles: roles.map(it => it.role) }];
		} else {
			return [];
		}
	})
);

export function isAllowedRuntimeModule(user: object, runtimeModuleName: string, manifest?: Manifest): boolean {
	if (!isUser(user) || !manifest) {
		return false;
	}

	const targetRuntimeModule = manifest.runtimeModules?.find(
		runtimeModule => runtimeModule.name === runtimeModuleName
	);
	if (!targetRuntimeModule?.roles || targetRuntimeModule.roles.length == 0) {
		return false;
	}

	if (isAdmin(user)) {
		return true;
	}

	const targetRuntimeModuleRoles = targetRuntimeModule.roles.map(role => role.name.toLowerCase());
	return selectStructuredRoles(user)
		.filter(role => role.key === manifest.identification.key)
		.some(role => {
			const roles: string[] = role.roles;
			return targetRuntimeModuleRoles.some(targetRuntimeModuleRole => roles.includes(targetRuntimeModuleRole));
		});
}

export function selectHasNeededPermissions(permissionPredicate: (p: string[]) => boolean, fvKey?: string) {
	return function (state: Record<string, unknown>) {
		if (selectIsAdmin(state)) {
			return true;
		}
		const permissions = selectPermissionsForFv(fvKey)(state);
		return permissionPredicate(permissions);
	};
}

export function getUserRoles(user: User, manifest: Manifest): string[] {
	return selectStructuredRoles(user)
		.filter(role => role.key === manifest.identification.key)
		.flatMap(e => e.roles);
}

export function getAllowedScopes(
	userRoles: string[],
	roles: RuntimeModuleRoleSpec[]
): RuntimeModuleRoleScopeSpec[] | undefined {
	if (roles.every(e => e.scopes === undefined)) {
		return undefined;
	}

	return roles
		.filter(moduleRole => userRoles.some(userRole => moduleRole.name.toLowerCase() === userRole))
		.flatMap(e => e.scopes ?? []);
}

export type UserInfo = { userId: string; userFullName: string };

export function getUserInfo(user: unknown): UserInfo | undefined {
	if (!user || typeof user !== "object" || !("profile" in user)) {
		return undefined;
	}
	const profile = user.profile;
	if (
		!profile ||
		typeof profile !== "object" ||
		!("preferred_username" in profile) ||
		!("given_name" in profile) ||
		!("family_name" in profile)
	) {
		return undefined;
	}

	const userId = profile.preferred_username;
	const firstName = profile.given_name;
	const lastName = profile.family_name;
	const userFullName = `${firstName} ${lastName}`;

	if (
		typeof userId === "string" &&
		userId.trim() &&
		typeof firstName === "string" &&
		firstName.trim() &&
		typeof firstName === "string" &&
		firstName.trim()
	) {
		return { userId, userFullName };
	}
	return undefined;
}
